import { useCallback, useEffect, useRef, useState } from 'react';
import { qubbase } from '@/lib/qubbase';
import type { Priority, Task, TaskStats, TaskWithAuthor } from '@/types';

export type TaskFilter = 'all' | 'mine' | 'open' | 'done';

/** Columns to read: the task plus its author, embedded through the tasks.user_id → profiles.id foreign key. */
const COLUMNS = 'id, user_id, title, done, priority, created_at, author:profiles(username, avatar_path)';

/**
 * The shared task board. Reads go through RLS (every signed-in user may read all tasks), writes are
 * limited by policy to the author's own tasks. A realtime subscription keeps every open tab in sync.
 */
export function useTasks(userId: string, filter: TaskFilter, search: string) {
  const [tasks, setTasks] = useState<TaskWithAuthor[]>([]);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const request = useRef(0);

  const loadStats = useCallback(async () => {
    // Postgres function public.task_stats(), called through the REST API.
    const { data, error } = await qubbase.rpc<TaskStats[]>('task_stats');
    if (!error && data?.[0]) {
      const s = data[0];
      setStats({ total: Number(s.total), done: Number(s.done), mine: Number(s.mine), mine_open: Number(s.mine_open) });
    }
  }, []);

  const load = useCallback(async () => {
    const id = ++request.current;
    let query = qubbase.from<TaskWithAuthor>('tasks').select<TaskWithAuthor>(COLUMNS).order('done').order('created_at', { ascending: false }).limit(200);
    if (filter === 'mine') query = query.eq('user_id', userId);
    if (filter === 'open') query = query.eq('done', false);
    if (filter === 'done') query = query.eq('done', true);
    if (search.trim()) query = query.ilike('title', `%${search.trim()}%`);
    const { data, error } = await query;
    if (id !== request.current) return; // a newer request superseded this one
    setLoading(false);
    if (error) return setError(error.message);
    setError(null);
    setTasks(data ?? []);
  }, [userId, filter, search]);

  useEffect(() => {
    void load();
    void loadStats();
  }, [load, loadStats]);

  // Realtime: any insert/update/delete on tasks that this user is allowed to read.
  const reload = useRef(load);
  reload.current = load;
  useEffect(() => {
    const channel = qubbase
      .channel('tasks-board')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => {
        void reload.current();
        void loadStats();
      })
      .subscribe((status) => setLive(status === 'SUBSCRIBED'));
    return () => {
      void qubbase.removeChannel(channel);
    };
  }, [loadStats]);

  const run = async (op: PromiseLike<{ error: { message: string } | null }>) => {
    const { error } = await op;
    if (error) throw new Error(error.message);
    await Promise.all([load(), loadStats()]);
  };

  return {
    tasks,
    stats,
    loading,
    error,
    live,
    addTask: (title: string, priority: Priority) => run(qubbase.from<Task>('tasks').insert({ title, priority })),
    toggleTask: (task: Task) => run(qubbase.from<Task>('tasks').update({ done: !task.done }).eq('id', task.id)),
    setPriority: (task: Task, priority: Priority) => run(qubbase.from<Task>('tasks').update({ priority }).eq('id', task.id)),
    renameTask: (task: Task, title: string) => run(qubbase.from<Task>('tasks').update({ title }).eq('id', task.id)),
    deleteTask: (task: Task) => run(qubbase.from<Task>('tasks').delete().eq('id', task.id)),
    clearCompleted: () => run(qubbase.from<Task>('tasks').delete().eq('user_id', userId).eq('done', true)),
  };
}
